'use client'

import { CachedNotifications, createConfigForNotifications } from '@/features/caching/config/tanstack'
import { Notification } from '@/features/notification/model/notification'
import NotificationService from '@/features/notification/notification-service'
import { TranslationBundles } from '@/features/sitecore/configs/translation-bundles'
import { useSitecoreState } from '@/features/sitecore/store/useSitecoreState'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { produce } from 'immer'
import { useState } from 'react'
import { NotificationTab, TabContent, Tabs } from '../uiComponents'
import { NotificationTabButton } from './NotificationTabButton'

interface NotificationsOverlayProps {
	open: boolean
	isReady: (isReady: boolean) => void
	notReadCount: (count: number) => void
}

export const NotificationsOverlay = (props: NotificationsOverlayProps) => {
	const [visibleTabIndex, setVisibleTabIndex] = useState<number>(0)

	const sitecoreStore = useSitecoreState((state) => state)
	const types = sitecoreStore.getLabel(TranslationBundles.NOTIFICATIONS, 'NOTIFICATION-ENABLED-TABS', 'all').split(',')
	const pageSize = sitecoreStore.getNumber(TranslationBundles.NOTIFICATIONS, 'NOTIFICATION-PAGE-SIZE', 10)

	const queryClient = useQueryClient()
	const typesWithoutAll = types.filter((type) => type !== 'all')
	const notificationsQuery = useInfiniteQuery(createConfigForNotifications(typesWithoutAll, pageSize))

	const retrievedNotifications = (notificationsQuery.data?.pages || []).flatMap((page) => page.notifications)
	const notRemoved = retrievedNotifications.filter((notification: Notification) => !notification.isRemoved)
	const notReadCount = notRemoved.filter((notification: Notification) => types.includes(notification.type) && !notification.isRead).length

	const allNotifications = notRemoved.filter((notification) => types.includes(notification.type))
	const notifications = notRemoved.reduce(
		(acc, notification) => {
			if (!acc[notification.type]) {
				acc[notification.type] = []
			}
			acc[notification.type].push(notification)
			return acc
		},
		{} as Record<string, Notification[]>
	)

	props.isReady(notificationsQuery.isSuccess)
	props.notReadCount(notReadCount)

	const handleRetrieveMoreNotifications = async () => {
		notificationsQuery.fetchNextPage()
	}

	const handleReadNotification = async (notificationId: number) => {
		await NotificationService.read({ notificationId })
		changeCachedNotification(notificationId, (notification) => (notification.isRead = true))
	}

	const handleCancelNotification = async (notificationId: number) => {
		await NotificationService.cancel({ notificationId })
		changeCachedNotification(notificationId, (notification) => (notification.isRemoved = true))
	}

	const changeCachedNotification = (notificationId: number, editor: (notification: Notification) => void) => {
		queryClient.setQueryData<CachedNotifications>(['notifications'], (oldData) => {
			if (!oldData) return oldData
			return produce(oldData, (draft) => {
				draft.pages.forEach((page) => {
					const found = page.notifications.find((n) => n.id === notificationId)
					if (found) editor(found)
				})
			})
		})
	}

	if (!props.open) return null

	return (
		<div className="absolute w-full md:w-auto left-0 md:left-auto top-100 flex justify-center cursor-default" onClick={(e) => e.stopPropagation()}>
			<div className="hidden md:block absolute top-0 z-20 w-2 h-2 bg-white -translate-y-1/2 rotate-45"></div>
			<div className="w-full md:min-w-96 md:max-w-xl min-h-44 bg-white rounded-md border border-background py-2 px-3">
				<Tabs variant="button" activeIndex={visibleTabIndex} onActiveIndexChanged={setVisibleTabIndex} tabListNoWrap={true}>
					{types.map((type, index) => {
						const currentNotifications = type === 'all' ? allNotifications : notifications[type]
						const button = <NotificationTabButton type={type} notifications={currentNotifications} isActive={visibleTabIndex === index} />
						return (
							<TabContent key={index} button={button}>
								<NotificationTab key={index} type={type} notifications={currentNotifications} retrieveMoreNotifications={() => handleRetrieveMoreNotifications()} read={handleReadNotification} cancel={handleCancelNotification} />
							</TabContent>
						)
					})}
				</Tabs>
			</div>
		</div>
	)
}
